import type { ActorRefFrom, AnyActorLogic } from 'xstate';
import z from 'zod';
import { optionSchema } from '~/features/types';
import type { MsaasListingGeography } from '~/types/hlx/msaasJobListing';

// What a selling listing offers (D19): license states for remote work, and a place plus a travel
// distance for in-person work. Which of the two applies follows the listing's work location.
export type CoverageWorkLocation = 'REMOTE' | 'ONSITE' | 'PARTIAL_REMOTE';

export const coversStates = (workLocation: string): boolean =>
  workLocation === 'REMOTE' || workLocation === 'PARTIAL_REMOTE';
export const coversPlace = (workLocation: string): boolean =>
  workLocation === 'ONSITE' || workLocation === 'PARTIAL_REMOTE';

// The picked place keeps what the geography search returned, because the save sends the reference
// row (the API derives the coordinates from it) and a bare react-select option would lose it.
export const coverageLocationSchema = optionSchema.extend({
  kind: z.enum(['ZIP', 'CITY']),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
});

export type CoverageLocationOption = z.infer<typeof coverageLocationSchema>;

export const CoverageFormSchema = z.object({
  states: z.array(optionSchema),
  location: coverageLocationSchema.nullable(),
  radiusMiles: z.string(),
  providerIds: z.array(z.number()),
});

export type CoverageFormValues = z.infer<typeof CoverageFormSchema>;

/** What one save writes. `null` / `persist*: false` mean "leave it alone". */
export type CoverageSubmitPayload = {
  stateCodes: string[];
  persistStates: boolean;
  geographies: MsaasListingGeography[];
  persistGeographies: boolean;
  /** `null` when the roster never loaded, so a failed read cannot wipe it. */
  providerIds: number[] | null;
  /** False when the roster is as it was read, so it is reported but not written again. */
  persistProviders?: boolean;
};

export interface Context {
  listingId: number;
  loading: boolean;
  parentRef?: ActorRefFrom<AnyActorLogic>;
}

export type OnLoadEvent = { type: 'ON_LOAD'; payload: { listingId: number } };
export type OnSubmitEvent = { type: 'ON_SUBMIT'; payload: CoverageSubmitPayload };
/** The wizard left the coverage step (closed, or moved on): an unfinished save must not report back. */
export type OnResetEvent = { type: 'ON_RESET' };
export type Events = OnLoadEvent | OnSubmitEvent | OnResetEvent;

export type Scope = { context: Context; event: Events };

export type CoverageSubmitResult = {
  stateCodes: string[];
  geographies: MsaasListingGeography[];
  /** Absent when the roster was not written, so the listings machine keeps what it knew. */
  providerIds?: number[];
};
